/**
 * Quiz-mount session-attribute capture for Google Data Manager API
 * (adIdentifiers.sessionAttributes) match-rate uplift.
 *
 * Captures the 6 ad-attribution fields the API needs (gad_campaignid, gad_source,
 * landing URL, referrer, UA, session_start_time_usec) plus gclid. Capture happens
 * ONCE at quiz mount — performance.timeOrigin is navigation-anchored regardless,
 * but URL params can be mutated by router guards later in the SPA lifecycle,
 * so locking the read at mount is defense-in-depth.
 *
 * Pairs with leadclump-flow's session-attributes module (issue #27) — same
 * field shape, same per-field fail-open. Two writers, one widened CH table
 * (funnel_session_attribution). Quiz writes on first-touch (Google Ad → quiz),
 * flow writes on email-flow landing (returning lead). Together both cover the
 * full population of sessions that will dispatch a conversion later.
 *
 * Resilience contract:
 *   - Each field is read inside its own try/catch. A throw inside one field
 *     returns "" for that field; siblings are unaffected.
 *   - Module-level cache makes captureSessionAttributes() idempotent. Second
 *     call returns the cached object without re-reading the DOM. Mirrors the
 *     cachedSessionId pattern in session.ts.
 *   - session_start_time_usec is emitted as a STRING. JS Number caps at 2^53;
 *     microseconds since epoch are already ~17 digits today.
 */

import type { SessionAttributes } from "../types";

export type { SessionAttributes };

const EMPTY: SessionAttributes = {
  gad_campaignid: "",
  gad_source: "",
  landing_page_url: "",
  landing_page_referrer: "",
  landing_page_user_agent: "",
  session_start_time_usec: "",
  gclid: "",
};

let cached: SessionAttributes | null = null;

// Pre-emptive caps so an attacker-crafted oversized URL/UA cannot push the
// createLead payload past the SQS 256KB hard limit (which would 400 the lead
// and silently drop a real conversion). BE will re-validate per-field, but
// the FE should never knowingly ship a payload that risks the wire limit.
const MAX_URL_LEN = 8192;
const MAX_REFERRER_LEN = 2048;
const MAX_UA_LEN = 1024;

function safeRead(fn: () => string): string {
  try {
    const v = fn();
    return typeof v === "string" ? v : "";
  } catch {
    return "";
  }
}

function trunc(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) : s;
}

export function captureSessionAttributes(): SessionAttributes {
  if (cached) return cached;

  const params = safeRead(() => window.location.search);
  const search = new URLSearchParams(params);

  const result: SessionAttributes = {
    gad_campaignid: search.get("gad_campaignid") ?? "",
    gad_source: search.get("gad_source") ?? "",
    landing_page_url: trunc(
      safeRead(
        () => window.location.origin + window.location.pathname + window.location.search
      ),
      MAX_URL_LEN
    ),
    landing_page_referrer: trunc(safeRead(() => document.referrer), MAX_REFERRER_LEN),
    landing_page_user_agent: trunc(safeRead(() => navigator.userAgent), MAX_UA_LEN),
    session_start_time_usec: safeRead(() => {
      // performance.timeOrigin is navigation-anchored — same value whether read
      // at mount or at submit. UInt64 emitted as string so JS Number precision
      // loss (2^53) never reaches the wire.
      const origin = performance.timeOrigin;
      if (!Number.isFinite(origin)) return "";
      return String(Math.floor(origin * 1000));
    }),
    gclid: search.get("gclid") ?? "",
  };

  cached = result;
  return result;
}

export function __resetSessionAttributesForTests(): void {
  cached = null;
}

export const __EMPTY_SESSION_ATTRIBUTES: SessionAttributes = EMPTY;
